import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Car, User, Package,
  X, MessageSquare, Circle, Palette,
} from "lucide-react";
import { toast } from "sonner";
import {
  computeInstallerHours,
  startOfMondayWeek,
  weeklyCapacityFromHours,
  DEFAULT_WORKDAY_HOURS,
  WEEKLY_CAPACITY_HOURS,
} from "../lib/workload";

/**
 * Full month-grid calendar with multi-day colored event bars.
 * - Days are 6x7 grid
 * - Jobs render as horizontal bars spanning scheduled_date -> scheduled_end_date
 * - Colors keyed by status
 * - Click a bar to see details in a slide-over
 */

const STATUS_COLORS = {
  unbooked:               { bar: "bg-slate-500/50 border-slate-400/40 text-white", dot: "bg-slate-400" },
  booked:                 { bar: "bg-blue-500/70 border-blue-400/50 text-white",   dot: "bg-blue-400" },
  in_store:               { bar: "bg-blue-500/70 border-blue-400/50 text-white",   dot: "bg-blue-400" },
  wash_down:              { bar: "bg-cyan-500/70 border-cyan-400/50 text-white",   dot: "bg-cyan-400" },
  disassembly:            { bar: "bg-amber-500/80 border-amber-400/60 text-black", dot: "bg-amber-400" },
  wrapping:               { bar: "bg-amber-500/80 border-amber-400/60 text-black", dot: "bg-amber-400" },
  assembly:               { bar: "bg-amber-500/80 border-amber-400/60 text-black", dot: "bg-amber-400" },
  ready_for_collection:   { bar: "bg-emerald-500/70 border-emerald-400/60 text-white", dot: "bg-emerald-400" },
  collected:              { bar: "bg-emerald-500/70 border-emerald-400/60 text-white", dot: "bg-emerald-400" },
  completed:              { bar: "bg-emerald-500/70 border-emerald-400/60 text-white", dot: "bg-emerald-400" },
  cancelled:              { bar: "bg-red-500/40 border-red-400/40 text-white line-through opacity-60", dot: "bg-red-400" },
};

function startOfMonthGrid(year, month) {
  // Return the Monday-based first cell of the calendar month grid.
  // Weeks now run Mon → Sun so the workshop week aligns with the
  // "workday hours" settings (5 workdays first, weekend at the end).
  const first = new Date(year, month, 1);
  const dow = first.getDay(); // 0 = Sunday, 1 = Monday, ...
  const shift = dow === 0 ? 6 : dow - 1; // Monday=0 shift, Sunday=6 shift
  const start = new Date(first);
  start.setDate(first.getDate() - shift);
  start.setHours(0, 0, 0, 0);
  return start;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function parseYMD(s) {
  if (!s) return null;
  // supports "YYYY-MM-DD" or ISO
  const clean = s.slice(0, 10);
  const [y, m, d] = clean.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Return Monday 00:00 of the week that contains `d` (weeks run Mon → Sun).
// startOfMondayWeek + WEEKLY_CAPACITY_HOURS + computeInstallerHours +
// weeklyCapacityFromHours + DEFAULT_WORKDAY_HOURS are all shared with the
// Dashboard overload alert via `../lib/workload`.

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function CalendarPage() {
  const now = new Date();
  const [monthOffset, setMonthOffset] = useState(0);
  // Continuous multi-month view — how many months to stack vertically
  // from `monthOffset`. Default 3 gives the current month + next two
  // so the shop can see quote-conversions and future bookings without
  // clicking through. "Load more months" button at the bottom extends.
  const [monthsShown, setMonthsShown] = useState(3);
  const [jobs, setJobs] = useState([]);
  const [installers, setInstallers] = useState([]);
  const [workdayHours, setWorkdayHours] = useState(DEFAULT_WORKDAY_HOURS);
  const [statusFilter, setStatusFilter] = useState("all");
  const [installerFilter, setInstallerFilter] = useState("all"); // "all" | "unassigned" | installer.id
  // Clicking a job on the calendar jumps to the full job detail page
  // (with every section — items, invoices, assignments, materials,
  // photos) instead of showing the slim summary popup that used to live
  // here.
  const navigate = useNavigate();

  useEffect(() => {
    // Only pull actual jobs (booked or archived) — leads/quotes don't belong on the calendar.
    api.get("/jobs", { params: { main_status: "job,archived" } }).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
    // Installer/assistant list so we can colour bars by assignee. Uses the
    // lean roster endpoint so office role also gets to filter.
    api.get("/users/roster").then(({ data }) => {
      setInstallers(data);
    }).catch(() => setInstallers([]));
    // Per-day working hours (drives workload maths — weekend = 0 by default).
    api.get("/settings/workday-hours").then(({ data }) => setWorkdayHours(data)).catch(() => {});
  }, []);

  // installer_id -> user (for color / name lookups on event bars)
  const installerLookup = useMemo(() => {
    const m = {};
    for (const u of installers) m[u.id] = u;
    return m;
  }, [installers]);

  // Per-installer booked hours for THIS calendar week (Mon 00:00 → Sun 23:59:59).
  // Powers the mini utilization bar next to each installer's swatch so admins
  // spot who's overloaded at a glance. Recomputes when jobs or roster change.
  const weekWindow = useMemo(() => {
    const start = startOfMondayWeek(new Date());
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, []);

  const workloadByInstaller = useMemo(() => {
    const out = {};
    for (const u of installers) {
      out[u.id] = computeInstallerHours(u.id, jobs, weekWindow.start, weekWindow.end, workdayHours);
    }
    return out;
  }, [installers, jobs, weekWindow, workdayHours]);

  // Effective weekly capacity — sum of the seven per-day caps from settings.
  // Falls back to 40h if the admin somehow zeroes every day.
  const weeklyCapacity = useMemo(() => {
    const total = weeklyCapacityFromHours(workdayHours);
    return total > 0 ? total : WEEKLY_CAPACITY_HOURS;
  }, [workdayHours]);

  const reload = () => {
    api.get("/jobs", { params: { main_status: "job,archived" } }).then(({ data }) => setJobs(data)).catch(() => {});
  };

  // Drag-to-reschedule: dragging a job bar onto another day updates its
  // scheduled_date (and shifts end_date by the same delta). Shows an Undo
  // toast for 5s that snaps back to the original date if clicked.
  const handleDrop = async (dropDate, ev) => {
    ev.preventDefault();
    const jobId = ev.dataTransfer.getData("job/id");
    if (!jobId) return;
    const job = jobs.find((j) => j.id === jobId);
    if (!job || !job.scheduled_date) return;
    const oldStart = parseYMD(job.scheduled_date);
    if (!oldStart) return;
    const deltaDays = Math.round((dropDate - oldStart) / 86400000);
    if (!deltaDays) return;
    const newStart = addDays(oldStart, deltaDays);
    const newEnd = job.scheduled_end_date ? addDays(parseYMD(job.scheduled_end_date), deltaDays) : newStart;
    const originalStart = job.scheduled_date;
    const originalEnd = job.scheduled_end_date || "";
    try {
      await api.put(`/jobs/${jobId}`, {
        ...job,
        scheduled_date: ymd(newStart),
        scheduled_end_date: ymd(newEnd),
      });
      reload();
      // 5-second undo toast — snaps the job back to its previous date on click.
      toast.success(`Moved to ${ymd(newStart)}`, {
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.put(`/jobs/${jobId}`, {
                ...job,
                scheduled_date: originalStart,
                scheduled_end_date: originalEnd,
              });
              toast("Reschedule undone", { description: `Back to ${originalStart}` });
              reload();
            } catch (err) {
              toast.error(err.response?.data?.detail || "Undo failed");
            }
          },
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reschedule");
    }
  };

  const viewDate = useMemo(() => {
    const d = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    return d;
  }, [monthOffset]);

  // Filter & normalize job events
  const events = useMemo(() => {
    return jobs
      .filter((j) => j.scheduled_date)
      .filter((j) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "scheduled") return ["unbooked", "booked", "in_store"].includes(j.sub_status);
        if (statusFilter === "in_progress") return ["wash_down", "disassembly", "wrapping", "assembly"].includes(j.sub_status);
        if (statusFilter === "completed") return ["ready_for_collection", "collected", "completed"].includes(j.sub_status);
        return true;
      })
      .filter((j) => {
        if (installerFilter === "all") return true;
        if (installerFilter === "unassigned") return !j.assigned_to;
        return j.assigned_to === installerFilter;
      })
      .map((j) => {
        const start = parseYMD(j.scheduled_date);
        const end = parseYMD(j.scheduled_end_date) || start;
        if (!start) return null;
        return { ...j, _start: start, _end: end < start ? start : end };
      })
      .filter(Boolean);
  }, [jobs, statusFilter, installerFilter]);

  // Build the list of month dates to render vertically. Starts from
  // `viewDate` (which honours monthOffset) and extends `monthsShown`
  // months forward so the shop can scroll continuously.
  const monthList = useMemo(() => {
    const out = [];
    for (let i = 0; i < monthsShown; i++) {
      out.push(new Date(viewDate.getFullYear(), viewDate.getMonth() + i, 1));
    }
    return out;
  }, [viewDate, monthsShown]);

  // Per-month grid computations moved into <MonthGrid> below so each
  // stacked month renders independently.

  const goToday = () => setMonthOffset(0);
  const prev = () => setMonthOffset((o) => o - 1);
  const next = () => setMonthOffset((o) => o + 1);

  const monthLabel = monthsShown > 1
    ? `${MONTH_NAMES[viewDate.getMonth()]} ${viewDate.getFullYear()} — ${MONTH_NAMES[monthList[monthList.length - 1].getMonth()]} ${monthList[monthList.length - 1].getFullYear()}`
    : `${MONTH_NAMES[viewDate.getMonth()]} ${viewDate.getFullYear()}`;
  const todayYmd = ymd(new Date());

  const counts = useMemo(() => {
    const c = { scheduled: 0, in_progress: 0, completed: 0 };
    for (const e of events) {
      if (["unbooked", "booked", "in_store"].includes(e.sub_status)) c.scheduled++;
      else if (["wash_down", "disassembly", "wrapping", "assembly"].includes(e.sub_status)) c.in_progress++;
      else if (["ready_for_collection", "collected", "completed"].includes(e.sub_status)) c.completed++;
    }
    return c;
  }, [events]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-label flex items-center gap-2"><CalendarIcon className="w-3 h-3" /> Schedule</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-white">Calendar</h1>
          <p className="text-slate-400 mt-2 text-sm">Colored bars represent bookings; drag your eye across days to see overlaps.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusFilterPill status="all" label={`All (${events.length})`} current={statusFilter} onClick={setStatusFilter} />
          <StatusFilterPill status="scheduled" label={`Scheduled (${counts.scheduled})`} current={statusFilter} onClick={setStatusFilter} />
          <StatusFilterPill status="in_progress" label={`In progress (${counts.in_progress})`} current={statusFilter} onClick={setStatusFilter} />
          <StatusFilterPill status="completed" label={`Done (${counts.completed})`} current={statusFilter} onClick={setStatusFilter} />
        </div>
      </div>

      {/* Toolbar */}
      <GlassPanel className="!p-4" animate={false}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <button data-testid="calendar-prev" onClick={prev} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-200"><ChevronLeft className="w-4 h-4" /></button>
            <button data-testid="calendar-today" onClick={goToday} className="text-xs px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-slate-200">Today</button>
            <button data-testid="calendar-next" onClick={next} className="w-9 h-9 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-200"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="font-heading text-2xl md:text-3xl text-white tracking-tight" data-testid="calendar-month-label">{monthLabel}</div>
          <div className="flex items-center gap-3 text-[10px] text-slate-400">
            <Legend color="bg-blue-400" label="Scheduled" />
            <Legend color="bg-amber-400" label="In progress" />
            <Legend color="bg-emerald-400" label="Done" />
          </div>
        </div>
      </GlassPanel>

      {/* Installer legend + filter — colour swatch per installer so admins can
          eyeball who's on which job at a glance. Click a swatch to filter.
          Each chip shows THIS week's booked hours so overloaded crew stand out. */}
      {installers.length > 0 && (
        <GlassPanel className="!p-3" animate={false} data-testid="calendar-installer-legend">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-1.5">
              <Palette className="w-3 h-3" /> Installers · workload this week
            </span>
            <span className="text-[10px] text-slate-500" data-testid="calendar-workload-window">
              {ymd(weekWindow.start)} → {ymd(weekWindow.end)} · cap {weeklyCapacity}h/wk
            </span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <InstallerChip
              testId="calendar-installer-all"
              active={installerFilter === "all"}
              onClick={() => setInstallerFilter("all")}
              label="All"
            />
            <InstallerChip
              testId="calendar-installer-unassigned"
              active={installerFilter === "unassigned"}
              onClick={() => setInstallerFilter("unassigned")}
              label="Unassigned"
              swatch="rgba(148,163,184,0.4)"
            />
            {installers.map((u) => {
              // Prefer the installer's personal override; fall back to the
              // shop-wide weekly cap so part-timers show accurate utilization.
              const perCap = (u.capacity_hours_per_week === null || u.capacity_hours_per_week === undefined || u.capacity_hours_per_week === "")
                ? null
                : Number(u.capacity_hours_per_week);
              const capForThis = Number.isFinite(perCap) ? perCap : weeklyCapacity;
              return (
                <InstallerChip
                  key={u.id}
                  testId={`calendar-installer-${u.id}`}
                  active={installerFilter === u.id}
                  onClick={() => setInstallerFilter(u.id)}
                  label={u.name || u.email}
                  swatch={u.color || "rgba(148,163,184,0.6)"}
                  dim={!u.color}
                  hours={workloadByInstaller[u.id] || 0}
                  capacity={capForThis > 0 ? capForThis : weeklyCapacity}
                />
              );
            })}
          </div>
        </GlassPanel>
      )}

      {/* Continuous months — stacked vertically so admins can scroll
          across multiple months without paging. `monthOffset` shifts the
          starting month; `monthsShown` grows via the "Load more" button. */}
      <div className="space-y-6">
        {monthList.map((mDate, idx) => (
          <MonthGrid
            key={`${mDate.getFullYear()}-${mDate.getMonth()}`}
            monthDate={mDate}
            events={events}
            todayYmd={todayYmd}
            installerLookup={installerLookup}
            onSelectEvent={(job) => navigate(`/jobs/${job.id}`)}
            onDropOnDay={handleDrop}
            isFirst={idx === 0}
            isLast={idx === monthList.length - 1}
          />
        ))}
        <div className="flex items-center justify-center">
          <button
            type="button"
            data-testid="calendar-load-more-months"
            onClick={() => setMonthsShown((n) => n + 3)}
            className="text-xs px-4 py-2 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-slate-200"
          >
            Load 3 more months
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MonthGrid                                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Renders one month's worth of days + event bars. Extracted so
 * CalendarPage can stack multiple months vertically for continuous
 * scrolling. Owns its own week + lane computations off the shared
 * `events` array so overlapping-bar layout stays correct within each
 * month independently.
 */
function MonthGrid({ monthDate, events, todayYmd, installerLookup, onSelectEvent, onDropOnDay, isFirst = true, isLast = true }) {
  const gridStart = useMemo(
    () => startOfMonthGrid(monthDate.getFullYear(), monthDate.getMonth()),
    [monthDate],
  );

  // First / last day of this month — used to strip weeks that would
  // duplicate the neighbouring month's boundary week when we stack
  // months vertically (Sept's last row = Oct's first row → skip Oct's).
  const monthFirst = useMemo(() => new Date(monthDate.getFullYear(), monthDate.getMonth(), 1), [monthDate]);
  const monthLast = useMemo(() => new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0), [monthDate]);

  const weeks = useMemo(() => {
    const out = [];
    for (let w = 0; w < 6; w++) {
      const days = [];
      for (let d = 0; d < 7; d++) {
        days.push(addDays(gridStart, w * 7 + d));
      }
      // Non-first month → drop weeks that start BEFORE this month's 1st
      //  (those days are already rendered as the previous month's tail).
      if (!isFirst && days[0] < monthFirst) continue;
      // Non-last month → drop weeks whose Monday sits AFTER this month's
      // last day (those days are the next month's territory).
      if (!isLast && days[0] > monthLast) continue;
      out.push(days);
    }
    return out;
  }, [gridStart, monthFirst, monthLast, isFirst, isLast]);

  const weekEventLayouts = useMemo(() => {
    return weeks.map((week) => {
      const weekStart = week[0];
      const weekEnd = week[6];
      const rowEvents = events.filter((e) => e._end >= weekStart && e._start <= weekEnd);
      rowEvents.sort((a, b) => {
        if (a._start - b._start !== 0) return a._start - b._start;
        return (b._end - b._start) - (a._end - a._start);
      });
      const lanes = [];
      const placed = [];
      for (const e of rowEvents) {
        const startCol = Math.max(0, Math.floor((e._start - weekStart) / 86400000));
        const endCol = Math.min(6, Math.floor((e._end - weekStart) / 86400000));
        let laneIdx = 0;
        while (laneIdx < lanes.length) {
          const collision = lanes[laneIdx].some((p) => !(endCol < p.startCol || startCol > p.endCol));
          if (!collision) break;
          laneIdx++;
        }
        if (!lanes[laneIdx]) lanes[laneIdx] = [];
        const item = { e, startCol, endCol, lane: laneIdx };
        lanes[laneIdx].push(item);
        placed.push(item);
      }
      return placed;
    });
  }, [weeks, events]);

  const label = `${MONTH_NAMES[monthDate.getMonth()]} ${monthDate.getFullYear()}`;

  return (
    <div data-testid={`calendar-month-${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`}>
      {/* Sticky month header — floats at the top of the viewport as you
          scroll through this month, then hands off to the next month's
          header on the way out. Reads like an infinite agenda. */}
      <div className="sticky top-0 z-20 -mx-1 md:-mx-2 mb-2 backdrop-blur-md bg-neutral-950/70 border-b border-white/5 px-2 md:px-3 py-2">
        <div className="font-heading text-lg md:text-xl text-white tracking-tight">{label}</div>
      </div>
      {/* Day-of-week header — full-width 7-column grid that mirrors the
          WeekRow layout below EXACTLY so every label sits centred over
          its own column. Rendered on every month for readability. */}
      <div className="grid grid-cols-7 mb-1 md:mb-2 px-1">
        {DOW.map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-[0.2em] text-slate-500 text-center py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1 md:gap-2" data-testid={`calendar-grid-${monthDate.getFullYear()}-${monthDate.getMonth() + 1}`}>
        {weeks.map((week, wIdx) => (
          <WeekRow
            key={wIdx}
            week={week}
            viewMonth={monthDate.getMonth()}
            todayYmd={todayYmd}
            placedEvents={weekEventLayouts[wIdx]}
            installerLookup={installerLookup}
            onSelectEvent={onSelectEvent}
            onDropOnDay={onDropOnDay}
          />
        ))}
      </div>
    </div>
  );
}

function WeekRow({ week, viewMonth, todayYmd, placedEvents, installerLookup, onSelectEvent, onDropOnDay }) {
  const laneCount = placedEvents.reduce((m, p) => Math.max(m, p.lane + 1), 0);
  const laneHeight = 22; // px per lane
  const barsHeight = Math.max(60, laneCount * (laneHeight + 4) + 4);
  const [dragOverYmd, setDragOverYmd] = React.useState(null);

  return (
    <div className="relative bg-white/[0.015] border border-white/5 rounded-2xl overflow-hidden">
      {/* Day cells (background layer) */}
      <div className="grid grid-cols-7">
        {week.map((day) => {
          const inMonth = day.getMonth() === viewMonth;
          const isToday = ymd(day) === todayYmd;
          const dYmd = ymd(day);
          const isOver = dragOverYmd === dYmd;
          return (
            <div
              key={day.toISOString()}
              data-testid={`calendar-day-${dYmd}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverYmd(dYmd); }}
              onDragLeave={() => setDragOverYmd((v) => (v === dYmd ? null : v))}
              onDrop={(e) => { setDragOverYmd(null); onDropOnDay?.(day, e); }}
              className={`border-r border-b border-white/5 last:border-r-0 px-2 pt-1.5 relative transition-colors ${inMonth ? "" : "opacity-40"} ${isOver ? "bg-white/[0.06] ring-1 ring-inset ring-white/30" : ""}`}
              style={{ minHeight: `${barsHeight + 30}px` }}
            >
              <div className={`inline-flex items-center justify-center text-xs w-6 h-6 rounded-full ${isToday ? "bg-white text-black font-bold" : "text-slate-300"}`}>
                {day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Event bars overlay (single grid so bars can span columns) */}
      <div className="absolute inset-0 pt-9 px-1.5 pointer-events-none">
        <div className="grid grid-cols-7 h-full gap-x-1 relative">
          {placedEvents.map((p, idx) => {
            const width = p.endCol - p.startCol + 1;
            const top = p.lane * (laneHeight + 4);
            const status = STATUS_COLORS[p.e.sub_status] || STATUS_COLORS.booked;
            const installer = p.e.assigned_to ? installerLookup?.[p.e.assigned_to] : null;
            const installerColor = installer?.color || "";
            // When we have an installer colour, tint the bar in that hue while
            // keeping a status-coloured left rail — so admins see WHO owns the
            // job at a glance AND the workshop stage without hunting.
            const barStyle = installerColor
              ? {
                  background: `linear-gradient(90deg, ${installerColor}55 0%, ${installerColor}33 100%)`,
                  borderColor: `${installerColor}80`,
                }
              : {};
            const initials = (installer?.name || installer?.email || "")
              .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
            return (
              <button
                key={p.e.id + "_" + idx}
                data-testid={`calendar-event-${p.e.id}`}
                draggable
                onDragStart={(ev) => {
                  ev.dataTransfer.setData("job/id", p.e.id);
                  ev.dataTransfer.effectAllowed = "move";
                }}
                onClick={() => onSelectEvent(p.e)}
                className={`absolute rounded-md border ${installerColor ? "text-white" : status.bar} text-[11px] font-medium truncate text-left hover:brightness-110 transition pointer-events-auto cursor-grab active:cursor-grabbing overflow-hidden flex items-stretch`}
                style={{
                  left: `calc((100% / 7) * ${p.startCol} + 2px)`,
                  width: `calc((100% / 7) * ${width} - 4px)`,
                  top: `${top}px`,
                  height: `${laneHeight}px`,
                  lineHeight: `${laneHeight - 4}px`,
                  ...barStyle,
                }}
                title={`${p.e.service || ""} · ${p.e.vehicle || ""}${installer ? ` — ${installer.name || installer.email}` : ""} — drag to reschedule`}
              >
                {/* Status-coloured left rail so stage stays visible even when
                    the main fill is the installer colour. */}
                <span
                  className={`shrink-0 w-1 ${status.dot}`}
                  aria-hidden
                />
                <span className="flex-1 min-w-0 px-2 truncate">
                  <span className="opacity-95">{p.e.vehicle || p.e.service}</span>
                  <span className="opacity-60 hidden sm:inline"> — {p.e.contact_name}</span>
                </span>
                {installer && installerColor && (
                  <span
                    data-testid={`calendar-event-initials-${p.e.id}`}
                    className="shrink-0 px-1.5 flex items-center text-[9px] font-bold uppercase tracking-wider bg-black/25"
                    title={installer.name || installer.email}
                  >
                    {initials || "•"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function InstallerChip({ testId, active, onClick, label, swatch, dim = false, hours, capacity }) {
  const hasWorkload = typeof hours === "number" && typeof capacity === "number" && capacity > 0;
  const pct = hasWorkload ? Math.min(100, Math.max(0, (hours / capacity) * 100)) : 0;
  // Traffic-light colouring: <60% clear, 60-90% amber, >90% red. Overloaded
  // crew jump out even when the whole month looks green in the grid.
  const barTint = pct >= 90
    ? "bg-red-500"
    : pct >= 60
      ? "bg-amber-400"
      : "bg-emerald-400";
  const hoursText = hasWorkload
    ? `${Number.isInteger(hours) ? hours : hours.toFixed(1)}h`
    : null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      title={hasWorkload ? `${hoursText} booked this week · capacity ${capacity}h` : label}
      className={`inline-flex items-center gap-2 text-[11px] pl-2 pr-2.5 py-1 rounded-full border transition ${
        active
          ? "bg-white text-black border-white font-semibold"
          : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]"
      } ${dim && !active ? "opacity-70" : ""}`}
    >
      {swatch !== undefined && (
        <span
          className="w-2.5 h-2.5 rounded-sm border border-white/20 shrink-0"
          style={{ background: swatch }}
        />
      )}
      <span className="truncate max-w-[110px]">{label}</span>
      {hasWorkload && (
        <span
          data-testid={`${testId}-workload`}
          className="flex items-center gap-1.5 pl-1.5 ml-0.5 border-l border-white/10"
        >
          <span className={`tabular-nums text-[10px] font-semibold ${active ? "text-black/80" : pct >= 90 ? "text-red-300" : pct >= 60 ? "text-amber-300" : "text-slate-300"}`}>
            {hoursText}
          </span>
          <span className={`w-10 h-1.5 rounded-full overflow-hidden ${active ? "bg-black/15" : "bg-white/10"}`}>
            <span
              className={`block h-full ${barTint} transition-[width]`}
              style={{ width: `${pct}%` }}
            />
          </span>
        </span>
      )}
    </button>
  );
}

function StatusFilterPill({ status, label, current, onClick }) {
  const active = current === status;
  return (
    <button
      data-testid={`calendar-filter-${status}`}
      onClick={() => onClick(status)}
      className={`text-xs px-3 py-1.5 rounded-full border transition ${
        active
          ? "bg-white text-black border-white font-semibold"
          : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
      }`}
    >
      {label}
    </button>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}

function JobDetail({ job, onClose, onChange }) {
  const status = STATUS_COLORS[job.sub_status] || STATUS_COLORS.booked;
  const [installers, setInstallers] = React.useState([]);
  React.useEffect(() => {
    api.get("/users").then(({ data }) => setInstallers(data.filter((u) => u.role === "installer" || u.role === "assistant"))).catch(() => {});
  }, []);
  const swapInstaller = async (id) => {
    const inst = installers.find((u) => u.id === id) || null;
    try {
      await api.put(`/jobs/${job.id}`, {
        ...job,
        assigned_to: id,
        assigned_to_name: inst?.name || inst?.email || "",
      });
      toast.success(inst ? `Reassigned to ${inst.name || inst.email}` : "Unassigned");
      onChange?.();
      onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reassign"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
      <div data-testid="calendar-event-detail" className="glass-strong rounded-3xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-label flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${status.dot}`} />
              {(job.sub_status || "booked").replace(/_/g, " ")}
            </div>
            <h3 className="font-heading text-2xl text-white mt-1">{job.service}</h3>
            <div className="text-sm text-slate-400 mt-1 flex items-center gap-1.5"><Car className="w-3.5 h-3.5" /> {job.vehicle}</div>
          </div>
          <button data-testid="calendar-event-detail-close" onClick={onClose} className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-300"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label">Customer</div>
            <div className="text-white text-sm mt-1">{job.contact_name}</div>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label">Installer</div>
            <select
              data-testid="calendar-installer-swap"
              value={job.assigned_to || ""}
              onChange={(e) => swapInstaller(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-2 py-1 mt-1 text-xs text-white focus:outline-none focus:border-white/30"
            >
              <option value="" className="bg-neutral-900">— unassigned —</option>
              {installers.map((u) => (
                <option key={u.id} value={u.id} className="bg-neutral-900">{u.name || u.email}</option>
              ))}
            </select>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label">Starts</div>
            <div className="text-white text-sm mt-1">{job.scheduled_date || "—"}</div>
          </div>
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label">Ends</div>
            <div className="text-white text-sm mt-1">{job.scheduled_end_date || job.scheduled_date || "—"}</div>
          </div>
        </div>

        <div>
          <div className="flex justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            <span>Progress</span><span>{job.progress || 0}%</span>
          </div>
          <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-400 to-emerald-400" style={{ width: `${job.progress || 0}%` }} />
          </div>
        </div>

        {job.materials?.length > 0 && (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label flex items-center gap-1"><Package className="w-3 h-3" /> Materials</div>
            <div className="flex flex-wrap gap-1 mt-2">
              {job.materials.map((m, i) => <span key={i} className="pill pill-muted text-[10px]">{m}</span>)}
            </div>
          </div>
        )}

        {job.notes && (
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
            <div className="text-label">Notes</div>
            <div className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{job.notes}</div>
          </div>
        )}
      </div>
    </div>
  );
}
